<script setup lang="ts">
/**
 * The template's alternating content blocks: a framed screenshot on one side,
 * a headline with a checklist and a link on the other. Two blocks, mirrored.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const blocks = computed(() => [
  {
    id: 'builder',
    image: '/modern.png',
    // Display size at the frame's max width, in the file's own proportions.
    imageWidth: 500,
    imageHeight: 706,
    eyebrow: t('marketing.showcase.builder.eyebrow'),
    title: t('marketing.showcase.builder.title'),
    text: t('marketing.showcase.builder.text'),
    points: [
      t('marketing.showcase.builder.p1'),
      t('marketing.showcase.builder.p2'),
      t('marketing.showcase.builder.p3'),
    ],
    linkLabel: t('marketing.showcase.builder.link'),
    to: isAuthenticated.value ? '/cv' : '/free-cv-builder',
    reverse: false,
  },
  {
    id: 'web',
    image: '/web-sofax.png',
    imageWidth: 500,
    imageHeight: 408,
    eyebrow: t('marketing.showcase.web.eyebrow'),
    title: t('marketing.showcase.web.title'),
    text: t('marketing.showcase.web.text'),
    points: [
      t('marketing.showcase.web.p1'),
      t('marketing.showcase.web.p2'),
      t('marketing.showcase.web.p3'),
    ],
    linkLabel: t('marketing.showcase.web.link'),
    to: '/online-cv',
    reverse: true,
  },
])
</script>

<template>
  <section class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <div
            v-for="block in blocks"
            :key="block.id"
            class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:gap-20"
          >
            <!-- Framed screenshot with the template's offset shadow -->
            <div
              class="relative mx-auto w-full max-w-[31.25rem]"
              :class="block.reverse ? 'lg:order-2' : ''"
            >
              <div class="absolute inset-0 translate-x-3 translate-y-3 rounded-[0.625rem] bg-ink dark:bg-accent" />
              <div class="relative overflow-hidden rounded-[0.625rem] border border-line bg-white dark:border-gray-700">
                <NuxtImg
                  :src="block.image"
                  :alt="block.title"
                  :width="block.imageWidth"
                  :height="block.imageHeight"
                  sizes="xs:400px md:500px"
                  format="webp"
                  quality="90"
                  class="w-full"
                  loading="lazy"
                />
              </div>
            </div>

            <div :class="block.reverse ? 'lg:order-1' : ''">
              <div class="eyebrow mb-5">{{ block.eyebrow }}</div>
              <h2 class="mb-5 !text-4xl md:!text-5xl">{{ block.title }}</h2>
              <p class="mb-8 text-lg">{{ block.text }}</p>

              <ul class="mb-10 space-y-4">
                <li v-for="point in block.points" :key="point" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-1 shrink-0 text-accent" />
                  <span class="text-base text-body dark:text-gray-300">{{ point }}</span>
                </li>
              </ul>

              <SofaxArrowLink :to="block.to" :label="block.linkLabel" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
